// 閲覧範囲（role 7 の User.e3_department・accessible_survey_seasons）の Excel 一括登録 — 「部署指定拡張」PJ Ph.2・Ph.4（2026-10）
// ------------------------------------------------------------------
// 「閲覧範囲」画面（E3DepMgrPage）の「テンプレートダウンロード」「Excel一括登録」で使う純関数群。
//
// テンプレート（縦型 = 1 行 1 部署）:
//   シート「部署指定」 … 行1〜2 = 記入例（A 列「例」）/ 行3 = 日本語ラベル / 行4 = 英字キー（= 実質ヘッダー）/ 行5〜 = 入力欄
//     列 = no / email / company（系列会社ありグループのみ）/ department
//   シート「組織リスト」 … 選択肢（今日時点の有効な確定組織表から作成）
//     系列会社あり: A 列 = 系列会社名 +「全系列会社」、B 列以降 = 会社ごとの部署（先頭は「（会社全体）」）。
//                   部署列は会社列の値で選択肢が切り替わる連鎖リスト（名前定義 DEPT_n + INDIRECT/MATCH）
//     系列会社なし: A 列 = 部署の経路
//   シート「シーズン指定」 … Ph.4。列 = no / email / season（1 行 1 シーズン。構成は部署指定と同じ 4 段）
//   シート「シーズンリスト」 … Ph.4。A 列 = シーズンの表示名（選択肢）/ B 列 = シーズン ID。
//     取り込み時は**アップロードされたファイルのこのシート**で表示名 → ID を変換する（同名シーズンが実在するため）
//   シート「回答状況指定」 … 2026-10 追加パッチ。列 = no / email / answer_engagement / answer_team / answer_other
//     （1 行 1 人。各列「閲覧可」「閲覧不可」。空欄の種類は変更しない）。診断の種類ごとに回答状況（だれが回答済み・未回答か）を
//     見られるか（User.answer_status_view。utils/answerStatusView.js）
//   シート「シーズン管理者一覧」 … 企業の role 7 ユーザー（email・氏名）。email 列のドロップダウンの参照先
//   シート「記入方法」 … 注意事項
// 部署・シーズンとも先頭の選択肢「（制限なし）」= その項目を空にする（= すべて）。
// シートに出てきた人の、そのシートの項目だけを置き換える（部署・シーズン・回答状況指定のうち出てきたもの）。
//
// 登録時の保存値（User.e3_department）は「会社名」/「会社名／所属…」（系列会社あり）または「所属…」（なし）。
// サーバー rcdr_server/utils/e3Department.js（validateE3BulkItems）と同じ値になるように組み立てる。
import ExcelJS from 'exceljs';
import { buildOrgListPaths, triggerBlobDownload, ymd8 } from './orgRosterHelper';
import { orgUnitsToCompanyNames } from './orgReportFilter';
import { E3_DEPARTMENT_MAX, normalizeE3Path, normalizeE3DepartmentList } from './e3Department';

const PATH_SEP = '／';
export const E3_BULK_SHEET = '部署指定';
export const E3_BULK_LIST_SHEET = '組織リスト';
export const E3_BULK_GUIDE_SHEET = '記入方法';
export const E3_BULK_USER_SHEET = 'シーズン管理者一覧';
export const E3_BULK_SEASON_SHEET = 'シーズン指定';
export const E3_BULK_SEASON_LIST_SHEET = 'シーズンリスト';
export const E3_BULK_ANSWER_SHEET = '回答状況指定';
// 回答状況指定の選択肢（閲覧可／閲覧不可）
export const ANSWER_VIEW = '閲覧可';
export const ANSWER_HIDE = '閲覧不可';
// 回答状況指定の種類（列のキー → サーバーの answer_status_view のキー。表示順）
export const ANSWER_KIND_COLUMNS = [
  { key: 'answer_engagement', kind: 'engagement', label: 'エンゲージメント' },
  { key: 'answer_team', kind: 'team', label: '360度系' },
  { key: 'answer_other', kind: 'other', label: 'その他' },
];
// 部署・シーズン共通の選択肢「（制限なし）」（その項目を空にする = すべてが対象）
export const NO_RESTRICTION = '（制限なし）';
// 取り込み時にデータシートとして扱わないシート
const NON_DATA_SHEETS = [E3_BULK_LIST_SHEET, E3_BULK_USER_SHEET, E3_BULK_GUIDE_SHEET, E3_BULK_SEASON_LIST_SHEET];
// 系列会社の選択肢「全系列会社」（登録時にその時点の全系列会社へ展開する。保存はしない）
export const ALL_COMPANIES = '全系列会社';
// 部署の選択肢「（会社全体）」（= その系列会社全体。保存値は会社名のみ）
export const WHOLE_COMPANY = '（会社全体）';
// 入力欄（データ検証を付ける行数）
const INPUT_FIRST_ROW = 5;
const INPUT_LAST_ROW = 1004;
// 英字ヘッダー行を探す範囲
const HEADER_SEARCH_LIMIT = 10;

// Excel セル配色（tokens.css と値を一致。Excel 内部色はバックエンド生成物と同扱い＝名簿テンプレートと同じ）
const FILL_HEADER = 'FFF2EBD5'; // --grey-10
const FONT_EXAMPLE = 'FF6B6760'; // --grey-65
const FONT_REQUIRED = 'FFB5302F'; // --red-active

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * テンプレート・取り込みの前提となる「組織表モデル」を作る。
 * 系列会社ありグループ（has_affiliates）では、組織表に系列会社ノード（level 0）が無ければ利用不可。
 * @param {Array} units 今日時点の有効な確定組織表のノード（GET /api/org/tree の units）
 * @param {{ hasAffiliates?: boolean }} [options]
 * @returns {{ available: boolean, reason?: string, affiliate: boolean,
 *   companies: Array<{ name: string, depts: string[] }>, paths: string[] }}
 */
export const buildE3BulkModel = (units, options = {}) => {
  const list = Array.isArray(units) ? units : [];
  if (list.length === 0) {
    return { available: false, reason: 'no_org', affiliate: false, companies: [], paths: [] };
  }
  if (options.hasAffiliates !== true) {
    return { available: true, affiliate: false, companies: [], paths: buildOrgListPaths(list) };
  }
  const names = orgUnitsToCompanyNames(list);
  if (names.length === 0) {
    return { available: false, reason: 'no_company_node', affiliate: true, companies: [], paths: [] };
  }
  const prefixed = buildOrgListPaths(list, { affiliate: true });
  const companies = names.map((name) => ({
    name,
    depts: prefixed
      .filter((p) => p.startsWith(`${name}${PATH_SEP}`))
      .map((p) => p.slice(name.length + PATH_SEP.length)),
  }));
  return { available: true, affiliate: true, companies, paths: [] };
};

const pad2 = (n) => ('0' + n).slice(-2);
const ymdSlash = (value) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

/**
 * シーズンの選択肢（表示名 + ID）を作る（Ph.4）。開始日の新しい順。
 * 表示名 = 「サーベイ名｜シーズン名（開始日〜）」。同じ表示名が重なる場合は 2 件目以降に「［2］」… を付ける。
 * @param {Array} seasons Group.survey_season（_id・season_name・survey_code・survey_name・survey_start_date）
 * @param {Map<string,string>|Object} [surveyNameByCode] survey_code → サーベイ名（原本。無ければシーズンの survey_name）
 * @returns {Array<{ label: string, id: string }>}
 */
export const buildSeasonChoices = (seasons, surveyNameByCode) => {
  const nameOf = (code) => {
    if (!surveyNameByCode) return '';
    if (typeof surveyNameByCode.get === 'function') return surveyNameByCode.get(code) || '';
    return surveyNameByCode[code] || '';
  };
  const list = (Array.isArray(seasons) ? seasons : [])
    .filter((s) => s && s._id)
    .slice()
    .sort((a, b) => new Date(b.survey_start_date || 0) - new Date(a.survey_start_date || 0));
  const used = new Map();
  return list.map((s) => {
    const survey = nameOf(s.survey_code) || s.survey_name || s.survey_code || '';
    const start = ymdSlash(s.survey_start_date);
    const base = `${survey}｜${s.season_name || ''}${start ? `（${start}〜）` : ''}`;
    const n = (used.get(base) || 0) + 1;
    used.set(base, n);
    return { label: n === 1 ? base : `${base}［${n}］`, id: String(s._id) };
  });
};

const columnDefs = (affiliate) => [
  { key: 'no', jp: 'No.', width: 6 },
  { key: 'email', jp: 'メールアドレス（ログインID）', width: 32, required: true },
  ...(affiliate ? [{ key: 'company', jp: '系列会社（選択式）', width: 24, required: true }] : []),
  { key: 'department', jp: '部署（選択式）', width: 40, required: true },
];

const seasonColumnDefs = [
  { key: 'no', jp: 'No.', width: 6 },
  { key: 'email', jp: 'メールアドレス（ログインID）', width: 32, required: true },
  { key: 'season', jp: 'シーズン（選択式）', width: 70, required: true },
];

const answerColumnDefs = [
  { key: 'no', jp: 'No.', width: 6 },
  { key: 'email', jp: 'メールアドレス（ログインID）', width: 32, required: true },
  { key: 'answer_engagement', jp: 'エンゲージメント（選択式）', width: 24 },
  { key: 'answer_team', jp: '360度系（360度診断・フォロワーシップ）（選択式）', width: 30 },
  { key: 'answer_other', jp: 'その他（選択式）', width: 24 },
];

// データシートの見出し（行1〜2 記入例 / 行3 日本語ラベル / 行4 英字キー）を書く
const writeSheetHead = (sheet, defs, examples) => {
  defs.forEach((c, i) => {
    sheet.getColumn(i + 1).width = c.width;
  });
  examples.forEach((values) => {
    const row = sheet.addRow(values);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { color: { argb: FONT_EXAMPLE }, italic: true };
    });
  });
  const jpRow = sheet.addRow(defs.map((c) => c.jp));
  jpRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const def = defs[colNumber - 1];
    cell.font = { bold: true, color: def && def.required ? { argb: FONT_REQUIRED } : undefined };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL_HEADER } };
  });
  const keyRow = sheet.addRow(defs.map((c) => c.key));
  keyRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL_HEADER } };
  });
  sheet.views = [{ state: 'frozen', ySplit: 4 }];
};

/**
 * 閲覧範囲（部署指定・シーズン指定）テンプレート（Excel）を作る。
 * @param {ReturnType<typeof buildE3BulkModel>} model
 * @param {{ users?: Array<{ email: string, name?: string }>, seasons?: Array<{ label: string, id: string }> }} [options]
 *   users = 企業のシーズン管理者（role 7）。email 列のドロップダウンの選択肢になる（0 件ならドロップダウンなし）
 *   seasons = buildSeasonChoices の結果（シーズン指定シートの選択肢。先頭に「（制限なし）」を足す）
 * @returns {ExcelJS.Workbook}
 */
export const buildE3DeptTemplateWorkbook = (model, options = {}) => {
  const affiliate = model.affiliate === true;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(E3_BULK_SHEET);
  const seasonSheet = workbook.addWorksheet(E3_BULK_SEASON_SHEET);
  const answerSheet = workbook.addWorksheet(E3_BULK_ANSWER_SHEET);
  const listSheet = workbook.addWorksheet(E3_BULK_LIST_SHEET);
  const seasonListSheet = workbook.addWorksheet(E3_BULK_SEASON_LIST_SHEET);
  const userSheet = workbook.addWorksheet(E3_BULK_USER_SHEET);
  const guideSheet = workbook.addWorksheet(E3_BULK_GUIDE_SHEET);
  const defs = columnDefs(affiliate);

  // ── シーズンリスト（A = 表示名 / B = シーズン ID。先頭は「（制限なし）」） ──
  const seasonChoices = [{ label: NO_RESTRICTION, id: '' }, ...(options.seasons || [])];
  seasonListSheet.getCell(1, 1).value = 'シーズン（選択肢）';
  seasonListSheet.getCell(1, 2).value = 'season_id';
  seasonChoices.forEach((c, i) => {
    seasonListSheet.getCell(i + 2, 1).value = c.label;
    seasonListSheet.getCell(i + 2, 2).value = c.id;
  });
  seasonListSheet.getRow(1).font = { bold: true };
  seasonListSheet.getColumn(1).width = 80;
  seasonListSheet.getColumn(2).width = 28;
  const seasonListRange = `'${E3_BULK_SEASON_LIST_SHEET}'!$A$2:$A$${seasonChoices.length + 1}`;

  // ── シーズン管理者一覧（email 列のドロップダウン用。1 行目は見出し） ──
  const seen = new Set();
  const users = (options.users || [])
    .map((u) => ({
      email: u && typeof u.email === 'string' ? u.email.trim() : '',
      name: u && u.name ? String(u.name).trim() : '',
    }))
    .filter((u) => {
      const key = u.email.toLowerCase();
      if (u.email === '' || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.email.localeCompare(b.email, 'ja'));
  userSheet.getCell(1, 1).value = 'email';
  userSheet.getCell(1, 2).value = '氏名';
  users.forEach((u, i) => {
    userSheet.getCell(i + 2, 1).value = u.email;
    userSheet.getCell(i + 2, 2).value = u.name;
  });
  userSheet.getRow(1).font = { bold: true };
  userSheet.getColumn(1).width = 36;
  userSheet.getColumn(2).width = 24;
  const emailListRange =
    users.length > 0 ? `'${E3_BULK_USER_SHEET}'!$A$2:$A$${users.length + 1}` : '';

  // ── 組織リスト（1 行目は見出し。選択肢は 2 行目から） ──
  let companyListRange = '';
  let deptListRange = '';
  if (affiliate) {
    const choices = [
      ...model.companies.map((c) => ({ ...c, items: [WHOLE_COMPANY, ...c.depts] })),
      { name: ALL_COMPANIES, items: [WHOLE_COMPANY] },
      { name: NO_RESTRICTION, items: [NO_RESTRICTION] },
    ];
    listSheet.getCell(1, 1).value = '系列会社';
    choices.forEach((c, i) => {
      listSheet.getCell(i + 2, 1).value = c.name;
      // 会社ごとの部署列（先頭は「（会社全体）」。全系列会社・制限なしは 1 択）
      const col = i + 2;
      const items = c.items;
      listSheet.getCell(1, col).value = c.name;
      items.forEach((p, r) => {
        listSheet.getCell(r + 2, col).value = p;
      });
      const letter = listSheet.getColumn(col).letter;
      workbook.definedNames.add(
        `'${E3_BULK_LIST_SHEET}'!$${letter}$2:$${letter}$${items.length + 1}`,
        `DEPT_${i + 1}`
      );
      listSheet.getColumn(col).width = 28;
    });
    listSheet.getColumn(1).width = 24;
    companyListRange = `'${E3_BULK_LIST_SHEET}'!$A$2:$A$${choices.length + 1}`;
  } else {
    const items = [NO_RESTRICTION, ...model.paths];
    listSheet.getCell(1, 1).value = '部署';
    items.forEach((p, i) => {
      listSheet.getCell(i + 2, 1).value = p;
    });
    listSheet.getColumn(1).width = 40;
    deptListRange = `'${E3_BULK_LIST_SHEET}'!$A$2:$A$${items.length + 1}`;
  }
  listSheet.getRow(1).font = { bold: true };

  // ── 部署指定シート（行1〜2 記入例 = グレー・斜体。A 列「例」は取り込み時にスキップ） ──
  const firstCompany = affiliate ? model.companies[0] : null;
  const examples = affiliate
    ? [
        ['例', 'sample01@example.com', firstCompany.name, firstCompany.depts[0] || WHOLE_COMPANY],
        ['例', 'sample01@example.com', ALL_COMPANIES, WHOLE_COMPANY],
      ]
    : [
        ['例', 'sample01@example.com', model.paths[0] || '本社'],
        ['例', 'sample01@example.com', model.paths[1] || model.paths[0] || '本社／営業部'],
      ];
  writeSheetHead(sheet, defs, examples);

  // ── シーズン指定シート ──
  writeSheetHead(seasonSheet, seasonColumnDefs, [
    ['例', 'sample01@example.com', (options.seasons && options.seasons[0] && options.seasons[0].label) || ''],
    ['例', 'sample02@example.com', NO_RESTRICTION],
  ]);

  // ── 回答状況指定シート（2026-10 追加パッチ） ──
  writeSheetHead(answerSheet, answerColumnDefs, [
    ['例', 'sample01@example.com', ANSWER_HIDE, ANSWER_VIEW, ANSWER_HIDE],
    ['例', 'sample02@example.com', ANSWER_VIEW, ANSWER_VIEW, ANSWER_VIEW],
  ]);

  // 行5〜: データ検証（リスト）。エラー表示は出さない（手入力・貼り付けも許可し、取り込み時に検証する）
  const emailCol = defs.findIndex((c) => c.key === 'email') + 1;
  const companyCol = defs.findIndex((c) => c.key === 'company') + 1;
  const deptCol = defs.findIndex((c) => c.key === 'department') + 1;
  const companyLetter = companyCol > 0 ? sheet.getColumn(companyCol).letter : '';
  for (let r = INPUT_FIRST_ROW; r <= INPUT_LAST_ROW; r++) {
    if (emailListRange) {
      sheet.getCell(r, emailCol).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [emailListRange],
      };
      seasonSheet.getCell(r, 2).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [emailListRange],
      };
      answerSheet.getCell(r, 2).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [emailListRange],
      };
    }
    [3, 4, 5].forEach((c) => {
      answerSheet.getCell(r, c).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${ANSWER_VIEW},${ANSWER_HIDE}"`],
      };
    });
    seasonSheet.getCell(r, 3).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [seasonListRange],
    };
    if (affiliate) {
      sheet.getCell(r, companyCol).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [companyListRange],
      };
      sheet.getCell(r, deptCol).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`INDIRECT("DEPT_"&MATCH($${companyLetter}${r},${companyListRange},0))`],
      };
    } else {
      sheet.getCell(r, deptCol).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [deptListRange],
      };
    }
  }

  // ── 記入方法 ──
  const guide = [
    '閲覧範囲（部署指定・シーズン指定・回答状況指定）一括登録テンプレートの記入方法',
    '',
    `・「${E3_BULK_SHEET}」シートには 1 行につき 1 部署、「${E3_BULK_SEASON_SHEET}」シートには 1 行につき 1 シーズンを、5 行目以降に記入してください（1〜2 行目は記入例です）。`,
    `・「${E3_BULK_ANSWER_SHEET}」シートには 1 行につき 1 人、診断の種類（エンゲージメント／360度系／その他）ごとに「${ANSWER_VIEW}」か「${ANSWER_HIDE}」を記入してください（空欄の種類は変更しません）。回答状況は「だれが回答済み・未回答か」のことです。エンゲージメント・その他は「メンバー回答状況」で選べるシーズン、360度系（360度診断・フォロワーシップ）は「チーム回答状況」のメニューに効きます。エンゲージメントとその他の両方を「${ANSWER_HIDE}」にすると「メンバー回答状況」のメニューも出なくなります（組織結果・個人結果一括ダウンロードは今までどおり。初期値はすべて「${ANSWER_VIEW}」）。`,
    '・どれか 1 つのシートだけでも、複数のシートでも登録できます。同じ人に複数の部署・シーズンを指定するときは、同じメールアドレスで行を分けて記入してください（部署は 1 人 50 件まで）。',
    '・対象はシーズン管理者（role 7）のみです。それ以外のユーザーはエラーになります。',
    '・部署指定は、組織結果（E³-Survey）の集計と「メンバー回答状況」「チーム回答状況」「個人結果一括ダウンロード」に出す人の部署の絞り込みです。シーズン指定は、各画面に表示するシーズンの絞り込みです（E³-Survey のシーズンは組織結果を見るシーズン。未設定の場合はすべてのシーズンが対象。1つでも選ぶと選んだシーズン以外は表示されません）。',
    `・「${NO_RESTRICTION}」を選ぶと、その項目の指定を空にします（すべての部署／すべてのシーズンが対象）。他の部署・シーズンと同時には指定できません。`,
    `・メールアドレスは「${E3_BULK_USER_SHEET}」シート（ダウンロード時点のシーズン管理者）から選択できます。`,
    `・シーズンの選択肢は「${E3_BULK_SEASON_LIST_SHEET}」シートにあります（このシートは取り込みに使うため、編集しないでください）。`,
    '・★登録すると、シートに記入した人の既存の指定（部署指定シートなら部署、シーズン指定シートならシーズン）はすべて削除され、ファイルの内容に置き換わります（回答状況指定シートは記入した種類だけ置き換わります）。',
    '・ファイルに記入していない人、記入していないシートの項目は変更されません。',
    ...(affiliate
      ? [
          '・先に「系列会社」を選ぶと、「部署」の選択肢がその系列会社の部署に切り替わります。',
          `・「${WHOLE_COMPANY}」を選ぶと、その系列会社全体を閲覧できるようになります。`,
          `・系列会社に「${ALL_COMPANIES}」を選ぶと、登録時点のすべての系列会社を会社全体で登録します（部署は「${WHOLE_COMPANY}」）。`,
          '　登録後に追加された系列会社は自動では追加されません。特定の系列会社だけ外したい場合は、登録後に画面から削除してください。',
        ]
      : []),
    `・部署の選択肢は「${E3_BULK_LIST_SHEET}」シートにあります（今日時点で有効な組織表から作成）。`,
  ];
  guide.forEach((text, i) => {
    guideSheet.getCell(i + 1, 1).value = text;
  });
  guideSheet.getCell(1, 1).font = { bold: true };
  guideSheet.getColumn(1).width = 110;

  return workbook;
};

/**
 * テンプレートを生成してダウンロードする。
 * @param {ReturnType<typeof buildE3BulkModel>} model
 * @param {string} groupCode ファイル名用
 * @param {{ users?: Array, seasons?: Array }} [options] buildE3DeptTemplateWorkbook 参照
 */
export const downloadE3DeptTemplate = async (model, groupCode, options = {}) => {
  // options = { users, seasons }（buildE3DeptTemplateWorkbook 参照）
  const workbook = buildE3DeptTemplateWorkbook(model, options);
  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    `閲覧範囲テンプレート_${groupCode}_${ymd8()}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

// ── 現在の登録内容の出力（Ph.5） ──
// テンプレートと同じ形のファイルに、シーズン管理者全員の現在の部署指定・シーズン指定を書き出す。
// そのまま取り込めば何も変わらない（指定なしの人は「（制限なし）」の 1 行で出力する）。
// 今日時点の組織表・企業のシーズン一覧に無い値はそのまま出力し、「確認」列に理由を書く（取り込むとエラーになる）。
const NOTE_COLUMN = { key: 'note', jp: '確認（取り込み時は無視）', width: 40 };

// 「確認」列の見出し（行3 日本語ラベル / 行4 英字キー）を最終列の右に足す
const addNoteColumn = (sheet, col) => {
  sheet.getColumn(col).width = NOTE_COLUMN.width;
  [
    [3, NOTE_COLUMN.jp],
    [4, NOTE_COLUMN.key],
  ].forEach(([r, value]) => {
    const cell = sheet.getCell(r, col);
    cell.value = value;
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL_HEADER } };
  });
};

// 保存値（経路）1 件をテンプレートの列（系列会社・部署）へ戻す
const splitCurrentPath = (path, model) => {
  const p = normalizeE3Path(path);
  if (!model.affiliate) {
    return { company: '', department: p, note: model.paths.includes(p) ? '' : '部署が今日時点の組織表にありません' };
  }
  const idx = p.indexOf(PATH_SEP);
  const company = idx < 0 ? p : p.slice(0, idx);
  const department = idx < 0 ? WHOLE_COMPANY : p.slice(idx + PATH_SEP.length);
  const found = model.companies.find((c) => c.name === company);
  let note = '';
  if (!found) note = '系列会社が今日時点の組織表にありません';
  else if (department !== WHOLE_COMPANY && !found.depts.includes(department)) {
    note = '部署が今日時点の組織表にありません';
  }
  return { company, department, note };
};

/**
 * 現在の登録内容をテンプレートと同じ形で作る（Ph.5）。
 * @param {ReturnType<typeof buildE3BulkModel>} model
 * @param {{
 *   users?: Array<{ email: string, name?: string, e3_department?: string[], accessible_survey_seasons?: Array,
 *     answer_status_view?: { engagement?: boolean, team?: boolean, other?: boolean } }>,
 *   seasons?: Array<{ label: string, id: string }>,
 *   asOf?: string,   // 出力日時の表示（記入方法シートに書く）
 * }} [options]
 *   users = 企業のシーズン管理者（role 7）。accessible_survey_seasons は ID 文字列でも populate 済みの文書でもよい
 * @returns {ExcelJS.Workbook}
 */
export const buildE3DeptCurrentWorkbook = (model, options = {}) => {
  const affiliate = model.affiliate === true;
  const seasons = options.seasons || [];
  const workbook = buildE3DeptTemplateWorkbook(model, { users: options.users, seasons });
  const deptSheet = workbook.getWorksheet(E3_BULK_SHEET);
  const seasonSheet = workbook.getWorksheet(E3_BULK_SEASON_SHEET);
  const answerSheet = workbook.getWorksheet(E3_BULK_ANSWER_SHEET);
  const defs = columnDefs(affiliate);
  const deptNoteCol = defs.length + 1;
  const seasonNoteCol = seasonColumnDefs.length + 1;
  addNoteColumn(deptSheet, deptNoteCol);
  addNoteColumn(seasonSheet, seasonNoteCol);

  const labelById = new Map(seasons.map((s) => [String(s.id).toLowerCase(), s.label]));
  const users = (options.users || [])
    .filter((u) => u && typeof u.email === 'string' && u.email.trim() !== '')
    .slice()
    .sort((a, b) => a.email.trim().toLowerCase().localeCompare(b.email.trim().toLowerCase()));

  let deptRow = INPUT_FIRST_ROW;
  let seasonRow = INPUT_FIRST_ROW;
  const writeDept = (email, company, department, note) => {
    const values = [deptRow - INPUT_FIRST_ROW + 1, email];
    if (affiliate) values.push(company);
    values.push(department);
    values.forEach((v, i) => {
      deptSheet.getCell(deptRow, i + 1).value = v;
    });
    if (note) deptSheet.getCell(deptRow, deptNoteCol).value = note;
    deptRow += 1;
  };
  const writeSeason = (email, label, note) => {
    [seasonRow - INPUT_FIRST_ROW + 1, email, label].forEach((v, i) => {
      seasonSheet.getCell(seasonRow, i + 1).value = v;
    });
    if (note) seasonSheet.getCell(seasonRow, seasonNoteCol).value = note;
    seasonRow += 1;
  };

  let answerRow = INPUT_FIRST_ROW;
  users.forEach((u) => {
    const email = u.email.trim();
    // 回答状況指定（値が無い既存ユーザー・値が無い種類は「閲覧可」）
    const view = u.answer_status_view || {};
    [
      answerRow - INPUT_FIRST_ROW + 1,
      email,
      ...ANSWER_KIND_COLUMNS.map((c) => (view[c.kind] === false ? ANSWER_HIDE : ANSWER_VIEW)),
    ].forEach((v, i) => {
      answerSheet.getCell(answerRow, i + 1).value = v;
    });
    answerRow += 1;
    const paths = normalizeE3DepartmentList(u.e3_department);
    if (paths.length === 0) {
      writeDept(email, NO_RESTRICTION, NO_RESTRICTION, '');
    } else {
      paths.forEach((p) => {
        const r = splitCurrentPath(p, model);
        writeDept(email, r.company, r.department, r.note);
      });
    }
    const ids = (Array.isArray(u.accessible_survey_seasons) ? u.accessible_survey_seasons : [])
      .map((s) => String(s && typeof s === 'object' && s._id ? s._id : s).toLowerCase())
      .filter((id, i, arr) => id && arr.indexOf(id) === i);
    if (ids.length === 0) {
      writeSeason(email, NO_RESTRICTION, '');
    } else {
      ids.forEach((id) => {
        const label = labelById.get(id);
        if (label) writeSeason(email, label, '');
        else writeSeason(email, `（不明なシーズン）${id}`, 'この企業のシーズン一覧にありません');
      });
    }
  });

  const guideSheet = workbook.getWorksheet(E3_BULK_GUIDE_SHEET);
  const asOf = options.asOf ? `${options.asOf} 時点の` : '現在の';
  guideSheet.getCell(2, 1).value =
    `★このファイルは${asOf}登録内容です。変更する人の行だけを残して直してから取り込んでください（そのまま取り込んでも内容は変わりません）。` +
    '「確認」列に記載のある行は、今日時点の組織表・シーズン一覧に無いため、そのまま取り込むとエラーになります。';
  guideSheet.getCell(2, 1).font = { bold: true };
  return workbook;
};

/**
 * 現在の登録内容を生成してダウンロードする（Ph.5）。
 * @param {ReturnType<typeof buildE3BulkModel>} model
 * @param {string} groupCode ファイル名用
 * @param {{ users?: Array, seasons?: Array, asOf?: string }} [options] buildE3DeptCurrentWorkbook 参照
 */
export const downloadE3DeptCurrent = async (model, groupCode, options = {}) => {
  const workbook = buildE3DeptCurrentWorkbook(model, options);
  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    `閲覧範囲_現在の設定_${groupCode}_${ymd8()}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

// セルの表示文字列（richText・数式結果も cell.text で吸収）
const cellText = (cell) => {
  if (!cell) return '';
  const text = cell.text;
  return text === null || text === undefined ? '' : String(text).trim();
};

// 'email' セルを含む行をヘッダーとして探し、その次の行から読む。A 列が「例」の行・全セル空欄の行はスキップ。
const readDataSheet = (sheet, keys) => {
  if (!sheet) return { headers: [], rows: [] };
  const lastRow = sheet.rowCount || 0;
  let headerRow = 0;
  for (let r = 1; r <= Math.min(HEADER_SEARCH_LIMIT, lastRow); r++) {
    const row = sheet.getRow(r);
    for (let c = 1; c <= (row.cellCount || 0); c++) {
      if (cellText(row.getCell(c)) === 'email') {
        headerRow = r;
        break;
      }
    }
    if (headerRow) break;
  }
  if (!headerRow) return { headers: [], rows: [] };
  const header = sheet.getRow(headerRow);
  const headers = [];
  const colByKey = {};
  for (let c = 1; c <= (header.cellCount || 0); c++) {
    const key = cellText(header.getCell(c));
    headers.push(key);
    if (key && colByKey[key] === undefined) colByKey[key] = c;
  }
  const read = (row, key) => (colByKey[key] ? cellText(row.getCell(colByKey[key])) : '');
  const rows = [];
  for (let r = headerRow + 1; r <= lastRow; r++) {
    const row = sheet.getRow(r);
    if (cellText(row.getCell(1)) === '例') continue;
    const record = { row: r };
    keys.forEach((key) => {
      record[key] = read(row, key);
    });
    if (keys.every((key) => record[key] === '')) continue;
    rows.push(record);
  }
  return { headers, rows };
};

/**
 * アップロードされたテンプレートを行の配列へ変換する。
 * - 部署: シート「部署指定」（無ければテンプレート付属シート以外の先頭シート＝旧テンプレート互換）
 * - シーズン: シート「シーズン指定」（Ph.4。無ければ空）+「シーズンリスト」（表示名 → シーズン ID）
 * - 回答状況指定: シート「回答状況指定」（2026-10 追加パッチ。無ければ空）
 * @param {ExcelJS.Workbook} workbook
 * @returns {{
 *   headers: string[], rows: Array<{ row, email, company, department }>,
 *   hasSeasonSheet: boolean, seasonHeaders: string[], seasonRows: Array<{ row, email, season }>,
 *   seasonMap: Map<string, string>,
 *   answerHeaders: string[], answerRows: Array<{ row, email, answer_engagement, answer_team, answer_other }>,
 * }}
 */
export const parseE3DeptWorkbook = (workbook) => {
  const sheets = workbook.worksheets || [];
  const deptSheet =
    workbook.getWorksheet(E3_BULK_SHEET) ||
    sheets.find(
      (ws) => ws && ![...NON_DATA_SHEETS, E3_BULK_SEASON_SHEET, E3_BULK_ANSWER_SHEET].includes(ws.name)
    );
  const seasonSheet = workbook.getWorksheet(E3_BULK_SEASON_SHEET);
  const answerSheet = workbook.getWorksheet(E3_BULK_ANSWER_SHEET);
  const dept = readDataSheet(deptSheet, ['email', 'company', 'department']);
  const season = readDataSheet(seasonSheet, ['email', 'season']);
  const answer = readDataSheet(answerSheet, ['email', ...ANSWER_KIND_COLUMNS.map((c) => c.key)]);
  const seasonMap = new Map();
  const listSheet = workbook.getWorksheet(E3_BULK_SEASON_LIST_SHEET);
  if (listSheet) {
    for (let r = 2; r <= (listSheet.rowCount || 0); r++) {
      const label = cellText(listSheet.getCell(r, 1));
      if (label === '' || seasonMap.has(label)) continue;
      seasonMap.set(label, cellText(listSheet.getCell(r, 2)));
    }
  }
  return {
    headers: dept.headers,
    rows: dept.rows,
    hasSeasonSheet: !!seasonSheet,
    seasonHeaders: season.headers,
    seasonRows: season.rows,
    seasonMap,
    answerHeaders: answer.headers,
    answerRows: answer.rows,
  };
};

/**
 * 取り込みに必要な列が揃っているか（行のあるシートだけ確認する）。
 * @param {ReturnType<typeof parseE3DeptWorkbook>} parsed
 * @returns {string} エラーメッセージ（問題なければ ''）
 */
export const validateE3DeptHeaders = (parsed, model) => {
  const missing = [];
  if (parsed.rows.length > 0) {
    const required = ['email', ...(model.affiliate ? ['company'] : []), 'department'];
    required.forEach((k) => {
      if (!(parsed.headers || []).includes(k)) missing.push(`${E3_BULK_SHEET}: ${k}`);
    });
  }
  if (parsed.seasonRows.length > 0) {
    ['email', 'season'].forEach((k) => {
      if (!(parsed.seasonHeaders || []).includes(k)) missing.push(`${E3_BULK_SEASON_SHEET}: ${k}`);
    });
  }
  if ((parsed.answerRows || []).length > 0) {
    ['email', ...ANSWER_KIND_COLUMNS.map((c) => c.key)].forEach((k) => {
      if (!(parsed.answerHeaders || []).includes(k)) missing.push(`${E3_BULK_ANSWER_SHEET}: ${k}`);
    });
  }
  if (missing.length === 0) return '';
  return `テンプレートの列（${missing.join(', ')}）が見つかりません。画面からダウンロードしたテンプレートを使用してください。`;
};

/**
 * 1 行分の部署を保存値（経路の配列）へ変換する。
 * @returns {{ paths?: string[], noRestriction?: boolean, usesAllCompanies?: boolean, error?: string }}
 */
const resolveRowPaths = (record, model) => {
  const dept = normalizeE3Path(record.department);
  if (!model.affiliate) {
    if (dept === '') return { error: '部署が未入力です' };
    if (dept === NO_RESTRICTION) return { paths: [], noRestriction: true };
    if (!model.paths.includes(dept)) return { error: `部署「${dept}」が組織表に存在しません` };
    return { paths: [dept] };
  }
  const company = normalizeE3Path(record.company);
  if (company === '') return { error: '系列会社が未入力です' };
  if (company === NO_RESTRICTION) {
    if (dept !== '' && dept !== NO_RESTRICTION) {
      return { error: `「${NO_RESTRICTION}」を選んだ場合、部署は「${NO_RESTRICTION}」のみ指定できます` };
    }
    return { paths: [], noRestriction: true };
  }
  const whole = dept === '' || dept === WHOLE_COMPANY;
  if (company === ALL_COMPANIES) {
    if (!whole) {
      return { error: `「${ALL_COMPANIES}」を選んだ場合、部署は「${WHOLE_COMPANY}」のみ指定できます` };
    }
    return { paths: model.companies.map((c) => c.name), usesAllCompanies: true };
  }
  const found = model.companies.find((c) => c.name === company);
  if (!found) return { error: `系列会社「${company}」が組織表に存在しません` };
  if (whole) return { paths: [company] };
  if (dept === NO_RESTRICTION) {
    return { error: `「${NO_RESTRICTION}」は系列会社にも「${NO_RESTRICTION}」を選んでください` };
  }
  // 会社名付きの経路（「A社／本社／…」）が貼り付けられた場合も受け付ける
  const relative = dept.startsWith(`${company}${PATH_SEP}`)
    ? dept.slice(company.length + PATH_SEP.length)
    : dept;
  if (!found.depts.includes(relative)) {
    return { error: `部署「${relative}」が系列会社「${company}」の組織表に存在しません` };
  }
  return { paths: [`${company}${PATH_SEP}${relative}`] };
};

/**
 * 1 行分のシーズンを ID へ変換する（Ph.4）。
 * @returns {{ id?: string, noRestriction?: boolean, error?: string }}
 */
const resolveRowSeason = (record, seasonMap) => {
  const label = (record.season || '').trim();
  if (label === '') return { error: 'シーズンが未入力です' };
  if (label === NO_RESTRICTION) return { noRestriction: true };
  const id = seasonMap.get(label);
  if (!id) return { error: `シーズン「${label}」が選択肢にありません` };
  return { id };
};

// 1 行分の回答状況指定を { 種類: 真偽値 } へ変換する（空欄の種類は入れない＝変更しない。2026-10 追加パッチ）
const resolveRowAnswer = (record) => {
  const view = {};
  for (const c of ANSWER_KIND_COLUMNS) {
    const v = (record[c.key] || '').trim();
    if (v === '') continue;
    if (v === ANSWER_VIEW) view[c.kind] = true;
    else if (v === ANSWER_HIDE) view[c.kind] = false;
    else return { error: `${c.label}は「${ANSWER_VIEW}」か「${ANSWER_HIDE}」を選んでください` };
  }
  if (Object.keys(view).length === 0) return { error: '回答状況指定が未入力です' };
  return { view };
};

// エラー一覧に出す回答状況指定の記入内容（例: 「エンゲージメント：閲覧不可、その他：閲覧可」）
const answerRecordText = (record) =>
  record
    ? ANSWER_KIND_COLUMNS.filter((c) => (record[c.key] || '').trim() !== '')
        .map((c) => `${c.label}：${record[c.key].trim()}`)
        .join('、')
    : '';

// エラー一覧の並び（シートの順 → 行番号）
const SHEET_ORDER = [E3_BULK_SHEET, E3_BULK_SEASON_SHEET, E3_BULK_ANSWER_SHEET];
export const compareE3BulkErrors = (a, b) =>
  SHEET_ORDER.indexOf(a.sheet) - SHEET_ORDER.indexOf(b.sheet) || (a.row || 0) - (b.row || 0);

/**
 * 取り込んだ行をメールアドレス単位にまとめ、行の検証をする。
 * - シートに出てきた項目だけを置き換える（部署指定シート → e3_department、シーズン指定シート → accessible_survey_seasons、
 *   回答状況指定シート → answer_status_view（記入した種類だけ））
 * - どちらかのシートにエラーの行がある人は登録対象から除外する（指定は丸ごと置き換えるため、一部の行だけ登録すると
 *   閲覧範囲が意図せず狭く・広くなる）
 * - 「（制限なし）」= その項目を空にする。他の値と同じ人に混在させるとエラー
 * @param {Array} rows parseE3DeptWorkbook の rows（部署指定シート）
 * @param {ReturnType<typeof buildE3BulkModel>} model
 * @param {{ seasonRows?: Array, seasonMap?: Map<string,string>, answerRows?: Array }} [options]
 * @returns {{
 *   items: Array<{ email, e3_department?: string[], accessible_survey_seasons?: string[],
 *     answer_status_view?: { engagement?: boolean, team?: boolean, other?: boolean }, rows: number[],
 *     usesAllCompanies: boolean }>,
 *   rowErrors: Array<{ sheet, row, email, company, department, season, answerStatus, error }>,
 *   excludedEmails: string[],
 * }}
 */
export const buildE3DeptItems = (rows, model, options = {}) => {
  const seasonRows = options.seasonRows || [];
  const seasonMap = options.seasonMap || new Map();
  const answerRows = options.answerRows || [];
  const groups = new Map(); // key = 小文字 email（最初に出てきた順）
  const rowErrors = [];
  const pushError = (sheet, record, email, error) =>
    rowErrors.push({
      sheet,
      row: record ? record.row : null,
      email,
      company: (record && record.company) || '',
      department: (record && record.department) || '',
      season: (record && record.season) || '',
      answerStatus: answerRecordText(record),
      error,
    });
  const groupOf = (email) => {
    const key = email.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, {
        email,
        rows: [],
        hasError: false,
        dept: null, // { paths, noRestriction, rows }
        season: null, // { ids, noRestriction, rows }
        answer: null, // { view, rows }（回答状況指定。view = 記入した種類だけ）
        usesAllCompanies: false,
      });
    }
    return groups.get(key);
  };
  // 共通: email の検証。グループを返す（未入力は null）
  const takeEmail = (sheet, record) => {
    const email = (record.email || '').trim();
    if (email === '') {
      pushError(sheet, record, email, 'メールアドレスが未入力です');
      return null;
    }
    const g = groupOf(email);
    g.rows.push(record.row);
    if (!EMAIL_PATTERN.test(email)) {
      g.hasError = true;
      pushError(sheet, record, email, 'メールアドレスの形式が正しくありません');
      return null;
    }
    return g;
  };

  (rows || []).forEach((record) => {
    const g = takeEmail(E3_BULK_SHEET, record);
    if (!g) return;
    if (!g.dept) g.dept = { paths: [], noRestriction: false, rows: [] };
    g.dept.rows.push(record.row);
    const resolved = resolveRowPaths(record, model);
    if (resolved.error) {
      g.hasError = true;
      pushError(E3_BULK_SHEET, record, g.email, resolved.error);
      return;
    }
    if (resolved.noRestriction) g.dept.noRestriction = true;
    g.dept.paths.push(...resolved.paths);
    if (resolved.usesAllCompanies) g.usesAllCompanies = true;
  });

  seasonRows.forEach((record) => {
    const g = takeEmail(E3_BULK_SEASON_SHEET, record);
    if (!g) return;
    if (!g.season) g.season = { ids: [], noRestriction: false, rows: [] };
    g.season.rows.push(record.row);
    const resolved = resolveRowSeason(record, seasonMap);
    if (resolved.error) {
      g.hasError = true;
      pushError(E3_BULK_SEASON_SHEET, record, g.email, resolved.error);
      return;
    }
    if (resolved.noRestriction) g.season.noRestriction = true;
    else if (!g.season.ids.includes(resolved.id)) g.season.ids.push(resolved.id);
  });

  answerRows.forEach((record) => {
    const g = takeEmail(E3_BULK_ANSWER_SHEET, record);
    if (!g) return;
    const resolved = resolveRowAnswer(record);
    if (resolved.error) {
      g.hasError = true;
      pushError(E3_BULK_ANSWER_SHEET, record, g.email, resolved.error);
      return;
    }
    if (!g.answer) g.answer = { view: {}, rows: [] };
    g.answer.rows.push(record.row);
    ANSWER_KIND_COLUMNS.forEach((c) => {
      if (resolved.view[c.kind] === undefined) return;
      if (g.answer.view[c.kind] !== undefined && g.answer.view[c.kind] !== resolved.view[c.kind]) {
        g.hasError = true;
        pushError(E3_BULK_ANSWER_SHEET, record, g.email,
          `同じ人の${c.label}に「${ANSWER_VIEW}」と「${ANSWER_HIDE}」の両方が記入されています`);
        return;
      }
      g.answer.view[c.kind] = resolved.view[c.kind];
    });
  });

  const items = [];
  const excludedEmails = [];
  groups.forEach((g) => {
    if (g.dept && g.dept.noRestriction && g.dept.paths.length > 0) {
      g.hasError = true;
      pushError(E3_BULK_SHEET, { row: g.dept.rows[0] }, g.email,
        `「${NO_RESTRICTION}」と他の部署は同時に指定できません`);
    }
    if (g.season && g.season.noRestriction && g.season.ids.length > 0) {
      g.hasError = true;
      pushError(E3_BULK_SEASON_SHEET, { row: g.season.rows[0] }, g.email,
        `「${NO_RESTRICTION}」と他のシーズンは同時に指定できません`);
    }
    const e3Department = g.dept ? normalizeE3DepartmentList(g.dept.paths) : null;
    if (!g.hasError && e3Department && e3Department.length > E3_DEPARTMENT_MAX) {
      g.hasError = true;
      pushError(E3_BULK_SHEET, { row: g.dept.rows[0] }, g.email,
        `部署が ${E3_DEPARTMENT_MAX} 件を超えています（${e3Department.length} 件）`);
    }
    if (g.hasError) {
      excludedEmails.push(g.email);
      return;
    }
    const item = { email: g.email, rows: g.rows, usesAllCompanies: g.usesAllCompanies };
    if (g.dept) item.e3_department = e3Department;
    if (g.season) item.accessible_survey_seasons = g.season.ids;
    if (g.answer) item.answer_status_view = g.answer.view;
    items.push(item);
  });
  rowErrors.sort(compareE3BulkErrors);
  return { items, rowErrors, excludedEmails };
};

const csvEscape = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * エラー一覧の CSV（BOM 付き UTF-8）。列 = シート,行番号,メールアドレス,系列会社,部署,シーズン,回答状況指定,エラー内容
 * @param {Array<{ sheet, row, email, company, department, season, answerStatus, error }>} errors
 */
export const buildE3DeptErrorCsv = (errors) => {
  const lines = [['シート', '行番号', 'メールアドレス', '系列会社', '部署', 'シーズン', '回答状況指定', 'エラー内容'].join(',')];
  (errors || []).forEach((e) => {
    lines.push(
      [
        e.sheet || '',
        e.row === null || e.row === undefined ? '' : e.row,
        e.email,
        e.company,
        e.department,
        e.season || '',
        e.answerStatus || '',
        e.error,
      ]
        .map(csvEscape)
        .join(',')
    );
  });
  return '\ufeff' + lines.join('\r\n') + '\r\n';
};
